import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useQuery } from '@apollo/client';
import orderBy from 'lodash.orderby';

import {
  ActionType,
  Flow,
  useBookingContext,
} from '@common/context/BookingContext';
import InputField from '@common/fields/InputField';
import SelectField from '@common/fields/SelectField';
import { useAutoscroll } from '@common/utils/hooks/useAutoScroll';
import useFeatureFlag from '@common/utils/hooks/useFeatureFlag';
import useQueryParams from '@common/utils/hooks/useQueryParams';
import { useScopedTranslation } from '@common/utils/hooks/useScopedTranslation';
import { ListingsQuery, SubjectsQuery } from '@typings/api';

import Section from '../_common/Section';
import SectionHeader from '../_common/SectionHeader';
import SectionHeaderSkeleton from '../_common/SectionHeaderSkeleton';
import { SelectFieldSkeleton } from '../_common/SelectFieldSkeleton';
import { MainFormFields } from '../BookingFlow';
import { LISTINGS, SUBJECTS } from '../queries';

export default function Subject() {
  const { t } = useScopedTranslation();
  const { id: contextId } = useQueryParams();
  const isListingsEnabled = useFeatureFlag('steps.listings.enabled');
  const wrapper = useRef(null);
  const { batch, change } = useForm();
  const {
    values: { leadSegmentId, listingId },
  } = useFormState();

  const [{ callbackEnabled, customerFormSubmissionCount }, dispatch] =
    useBookingContext();

  const { data: listingsData } = useQuery<ListingsQuery>(LISTINGS, {
    variables: {
      contextId,
    },
    skip: !isListingsEnabled,
  });
  const listings = useMemo(() => listingsData?.listings ?? [], [listingsData]);

  const { data, loading } = useQuery<SubjectsQuery>(SUBJECTS, {
    variables: {
      leadSegmentId,
      listingId,
    },
    skip: !leadSegmentId || customerFormSubmissionCount === 0,
    fetchPolicy: 'network-only',
  });
  const subjects = useMemo(
    () => orderBy(data?.subjects ?? [], ['category.order', 'order']),
    [data],
  );

  const handleChange = useCallback(
    (subjectId: string) => {
      return batch(() => {
        change(MainFormFields.MEETING_TYPE, undefined);
        change(MainFormFields.OFFICES, undefined);
        change(MainFormFields.CONTACT, undefined);
        change(MainFormFields.TIME_SLOT, undefined);
        change(MainFormFields.SUBJECT, subjectId);
      });
    },
    [batch, change],
  );

  useEffect(() => {
    const noSubjects = data && subjects.length === 0;
    const noListings = isListingsEnabled
      ? listingsData && listings.length <= 1
      : true;

    if (noListings && noSubjects && callbackEnabled) {
      return dispatch({
        type: ActionType.SET_FLOW,
        payload: Flow.FORCED_REFERRAL,
      });
    }

    if (subjects.length === 1) {
      return handleChange(subjects[0].id);
    }
  }, [
    data,
    subjects,
    listingsData,
    listings,
    listingId,
    isListingsEnabled,
    callbackEnabled,
    dispatch,
    handleChange,
  ]);

  useAutoscroll(wrapper);

  if (loading) {
    return (
      <Section ref={wrapper}>
        <SectionHeaderSkeleton />
        <SelectFieldSkeleton />
      </Section>
    );
  }

  if (subjects.length === 1) {
    return null;
  }

  if (subjects.length === 0) {
    return (
      <Section>
        <SectionHeader>{t('main_form.subject.question')}</SectionHeader>
        <p>{t('main_form.no_actions_possible')}</p>
      </Section>
    );
  }

  return (
    <Section ref={wrapper}>
      <SectionHeader>{t('main_form.subject.question')}</SectionHeader>
      <InputField name={MainFormFields.SUBJECT}>
        {(input) => (
          <SelectField {...input} onChange={handleChange}>
            {subjects.map(({ category, id, title }) => (
              <option key={id} value={id}>
                {category.name} - {title}
              </option>
            ))}
          </SelectField>
        )}
      </InputField>
    </Section>
  );
}
